import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { api } from '../api/client.js';
import { Weight, type ICategory, type ICheck, type ILabel, type IMappingRule, type IPlaywrightInput, type IScanLevel } from '../../shared/types.js';

type Status = 'idle' | 'loading' | 'failed';

export interface CheckConfigState {
  categories: ICategory[];
  checks: ICheck[];
  labels: ILabel[];
  scanLevels: IScanLevel[];
  playwrightInputs: IPlaywrightInput[];
  selectedCheckId: string | null;
  isDetailOpen: boolean;
  searchQuery: string;
  activeWeightFilter: Weight | null;
  activeLabelFilter: string | null;
  status: Status;
  error: string | null;
}

const initialState: CheckConfigState = {
  categories: [],
  checks: [],
  labels: [],
  scanLevels: [],
  playwrightInputs: [],
  selectedCheckId: null,
  isDetailOpen: false,
  searchQuery: '',
  activeWeightFilter: null,
  activeLabelFilter: null,
  status: 'idle',
  error: null,
};

export const fetchCheckConfig = createAsyncThunk('checkConfig/fetchAll', async (_, { rejectWithValue }) => {
  const [checksRes, categoriesRes, labelsRes, scanLevelsRes, playwrightRes] = await Promise.all([
    api.get<{ checks: ICheck[] }>('/config/checks'),
    api.get<{ categories: ICategory[] }>('/config/categories'),
    api.get<{ labels: ILabel[] }>('/config/labels'),
    api.get<{ scan_levels: IScanLevel[] }>('/config/scan-levels'),
    api.get<{ playwright_inputs: IPlaywrightInput[] }>('/config/playwright-inputs'),
  ]);

  if (!checksRes.success) return rejectWithValue(checksRes.error.message);
  if (!categoriesRes.success) return rejectWithValue(categoriesRes.error.message);
  if (!labelsRes.success) return rejectWithValue(labelsRes.error.message);
  if (!scanLevelsRes.success) return rejectWithValue(scanLevelsRes.error.message);
  if (!playwrightRes.success) return rejectWithValue(playwrightRes.error.message);

  return {
    checks: checksRes.data.checks,
    categories: categoriesRes.data.categories,
    labels: labelsRes.data.labels,
    scanLevels: scanLevelsRes.data.scan_levels,
    playwrightInputs: playwrightRes.data.playwright_inputs,
  };
});

export const updateCheck = createAsyncThunk(
  'checkConfig/updateCheck',
  async ({ id, patch }: { id: string; patch: Partial<ICheck> }, { rejectWithValue }) => {
    const res = await api.patch(`/config/checks/${id}`, patch);
    if (!res.success) return rejectWithValue(res.error.message);
    return { id, patch };
  },
);

export const assignLabels = createAsyncThunk(
  'checkConfig/assignLabels',
  async ({ id, labelIds }: { id: string; labelIds: number[] }, { rejectWithValue }) => {
    const res = await api.put(`/config/checks/${id}/labels`, { label_ids: labelIds });
    if (!res.success) return rejectWithValue(res.error.message);
    return { id, labelIds };
  },
);

export const updateMappingRuleOrder = createAsyncThunk(
  'checkConfig/updateMappingRuleOrder',
  async ({ ruleId, sortOrder }: { ruleId: number; sortOrder: number }, { rejectWithValue }) => {
    const res = await api.patch(`/config/mapping-rules/${ruleId}`, { sort_order: sortOrder });
    if (!res.success) return rejectWithValue(res.error.message);
    return { ruleId, sortOrder };
  },
);

const checkConfigSlice = createSlice({
  name: 'checkConfig',
  initialState,
  reducers: {
    setSelectedCheck(state, action: PayloadAction<string | null>) {
      state.selectedCheckId = action.payload;
      state.isDetailOpen = Boolean(action.payload);
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
    },
    setActiveWeightFilter(state, action: PayloadAction<Weight | null>) {
      state.activeWeightFilter = action.payload;
    },
    setActiveLabelFilter(state, action: PayloadAction<string | null>) {
      state.activeLabelFilter = action.payload;
    },
    reorderChecksInMemory(state, action: PayloadAction<{ checkId: string; targetCategoryId: string }>) {
      const found = state.checks.find((check) => check.id === action.payload.checkId);
      if (found) {
        found.category_id = action.payload.targetCategoryId;
      }
    },
    reorderMappingRulesInMemory(state, action: PayloadAction<{ checkId: string; rules: IMappingRule[] }>) {
      const found = state.checks.find((check) => check.id === action.payload.checkId);
      if (found) {
        found.mapping_rules = action.payload.rules;
      }
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCheckConfig.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCheckConfig.fulfilled, (state, action) => {
        state.status = 'idle';
        state.checks = action.payload.checks;
        state.categories = action.payload.categories;
        state.labels = action.payload.labels;
        state.scanLevels = action.payload.scanLevels;
        state.playwrightInputs = action.payload.playwrightInputs;
      })
      .addCase(fetchCheckConfig.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(updateCheck.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(assignLabels.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateMappingRuleOrder.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedCheck,
  setSearchQuery,
  setActiveWeightFilter,
  setActiveLabelFilter,
  reorderChecksInMemory,
  reorderMappingRulesInMemory,
  setError,
} = checkConfigSlice.actions;

export default checkConfigSlice.reducer;
